import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import type { OrderFormValues } from "../../types/forms";
import OrderDetails from "./sections/orderDetails";
import ContactInformation from "./sections/contactInformation";
import PartInformation from "./sections/partInformation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getOrderById, saveOrder } from "../../api/orderService";
import { useEffect, useRef } from "react";
import { cleanPayload } from "../../utils/cleanPayload";
import { ANALYTICS_EVENTS, trackEvent } from "../../lib/analytics";

export default function OrderPage() {
  const methods = useForm<OrderFormValues>();
  const { reset } = methods;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const pageEntryTime = useRef<number>(0);
  const didSubmit = useRef(false);

  useEffect(() => {
    pageEntryTime.current = Date.now();
    return () => {
      const timeSpentSeconds = Math.round(
        (Date.now() - pageEntryTime.current) / 1000,
      );
      if (!didSubmit.current) {
        trackEvent(ANALYTICS_EVENTS.ORDER_PAGE_DROP_OFF, {
          time_on_page_seconds: timeSpentSeconds,
        });
      }
    };
  }, []);

  const orderId = searchParams.get("id");

  const { data } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrderById(orderId!),
    enabled: !!orderId,
  });

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        addressInfo: {
          isReturnAddressDifferent:
            data.addressInfo?.isReturnAddressDifferent ?? false,
          shippingInfo: data.addressInfo?.shippingInfo ?? undefined,
        },
      });
    }
  }, [data, reset]);

  const { mutate: saveOrderMutation, isPending } = useMutation({
    mutationFn: async ({
      payload,
      imageFiles,
    }: {
      payload: Record<string, unknown>;
      imageFiles?: Record<string, File>;
    }) => {
      await saveOrder(payload, imageFiles);
    },
    onSuccess: () => {
      const timeSpentSeconds = Math.round(
        (Date.now() - pageEntryTime.current) / 1000,
      );
      didSubmit.current = true;
      trackEvent(ANALYTICS_EVENTS.ORDER_SUBMITTED, {
        time_on_page_seconds: timeSpentSeconds,
      });
      navigate(`/shipping-status?id=${orderId}`);
    },
  });

  const onSubmit = (formData: OrderFormValues) => {
    const { partImageFile, ...restFormData } = formData;

    const rawPayload = {
      ...restFormData,
      orderId,
      addressInfo: restFormData.addressInfo
        ? {
            ...restFormData.addressInfo,
            shippingInfo: restFormData.addressInfo.isReturnAddressDifferent
              ? restFormData.addressInfo.shippingInfo
              : null,
          }
        : undefined,
    };

    const payload = cleanPayload(rawPayload) as Record<string, unknown>;
    const targetLineItemId = restFormData.orderLineItems?.[0]?.orderLineItemId;
    const imageFiles =
      partImageFile && targetLineItemId
        ? { [targetLineItemId]: partImageFile }
        : undefined;
    saveOrderMutation({
      payload,
      imageFiles,
    });
  };

  const isReadOnly = !!data?.trackingInfo?.trackingNumber;
  const shouldRedirect =
    isReadOnly || !!data?.orderLineItems?.[0]?.partInfo?.partNumber;

  useEffect(() => {
    if (shouldRedirect && !location.state?.fromStatus) {
      navigate(`/shipping-status?id=${orderId}`, { replace: true });
    }
  }, [shouldRedirect, location.state, navigate, orderId]);

  return (
    <div className="my-7 mx-auto max-w-4xl rounded-xl">
      <div className="md:px-14 md:py-12 px-4 py-6">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <fieldset
              disabled={isReadOnly}
              className={
                isReadOnly ? "opacity-50 pointer-events-none select-none" : ""
              }
            >
              <OrderDetails />
              <PartInformation />
              <ContactInformation />
            </fieldset>

            <div className="md:mt-12 mt-8">
              {isReadOnly ? (
                <button
                  type="button"
                  onClick={() => navigate(`/shipping-status?id=${orderId}`)}
                  className="w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  Go to Status page
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isPending}
                  className="w-full h-13 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed transition-colors"
                >
                  {isPending ? "Saving..." : "Update Order Details"}
                </button>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
}
